import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { GraduationCap, ArrowRight, CheckCircle2 } from 'lucide-react';
import apiClient from '@/core/api/client';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import { theme } from '@/theme';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from '@/shared/components';
import { strongPassword } from '@/shared/utils/password';

const studentRegisterSchema = z.object({
  full_name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  password: strongPassword,
  preferred_country: z.string().min(1, 'Please select a preferred study destination')
});

type StudentRegisterInput = z.infer<typeof studentRegisterSchema>;

export const StudentRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [countries, setCountries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<StudentRegisterInput>({
    resolver: zodResolver(studentRegisterSchema),
    defaultValues: {
      full_name: '',
      email: '',
      password: '',
      preferred_country: ''
    }
  });

  useEffect(() => {
    // Fetch countries for preferred country dropdown
    apiClient.get(API_ENDPOINTS.PUBLIC.COUNTRIES)
      .then(res => setCountries(res.data?.data || res.data || []))
      .catch(err => console.error('Failed to load countries', err));
  }, []);

  const onSubmit = async (data: StudentRegisterInput) => {
    setLoading(true);
    try {
      const res = await apiClient.post(API_ENDPOINTS.PUBLIC.REGISTER_STUDENT, data);
      const tokens = res.data?.data || res.data;
      
      if (tokens.access_token) {
        localStorage.setItem('auth_token', tokens.access_token);
        if (tokens.refresh_token) {
          localStorage.setItem('refresh_token', tokens.refresh_token);
        }
        toast.success('Registration successful! Welcome to the portal.');
        await checkAuth();
        navigate('/dashboard');
      } else {
        throw new Error('Access token was not returned');
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.error?.message || err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: theme.colors.bg, minHeight: '100vh', padding: '120px 32px 80px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="page-enter">
      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: '80px', maxWidth: '1100px', width: '100%' }}>
        
        {/* Left Info Panel */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(115, 103, 240, 0.08)',
            color: theme.colors.primaryDark,
            padding: '8px 18px',
            borderRadius: '20px',
            fontSize: '0.8rem',
            fontWeight: 600,
            marginBottom: '24px',
            width: 'fit-content',
            border: '1px solid rgba(115, 103, 240, 0.18)'
          }}>
            <GraduationCap size={14} /> Student Admissions Portal
          </div>
          
          <h1 style={{
            fontSize: '2.8rem',
            fontWeight: 800,
            color: theme.colors.text.heading,
            lineHeight: 1.2,
            marginBottom: '24px',
            letterSpacing: '-0.5px'
          }}>
            Begin Your International<br/>
            <span style={{ color: theme.colors.primary }}>Education Voyage</span>
          </h1>
          
          <p style={{
            fontSize: '1.05rem',
            color: theme.colors.text.muted,
            lineHeight: 1.6,
            marginBottom: '36px'
          }}>
            Register your student candidate account to unlock automated program matching, manage admission folders, and coordinate directly with visa counselors.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[
              'Match credentials to 850+ global universities',
              'Organize academic transcript checklist folders',
              'Direct pipeline communication with counseling experts',
              'Full data privacy and profile isolation protection'
            ].map((feature, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.925rem', color: theme.colors.text.body }}>
                <CheckCircle2 size={16} color="var(--clr-success)" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Form Card */}
        <div className="card" style={{ padding: '40px' }}>
          <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--clr-text-heading)', marginBottom: '24px' }}>
            Create Student Account
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="form-group">
              <label className="form-label" htmlFor="full_name">Full Name *</label>
              <input
                id="full_name"
                type="text"
                placeholder="e.g. Alice Walker"
                className={`form-input ${errors.full_name ? 'form-input--error' : ''}`}
                {...register('full_name')}
              />
              {errors.full_name && <span className="form-error">{errors.full_name.message}</span>}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="email">Email Address *</label>
              <input
                id="email"
                type="email"
                placeholder="e.g. alice@student.com"
                className={`form-input ${errors.email ? 'form-input--error' : ''}`}
                {...register('email')}
              />
              {errors.email && <span className="form-error">{errors.email.message}</span>}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="password">Login Password *</label>
              <input
                id="password"
                type="password"
                placeholder="Choose a secure password"
                className={`form-input ${errors.password ? 'form-input--error' : ''}`}
                {...register('password')}
              />
              {errors.password && <span className="form-error">{errors.password.message}</span>}
            </div>

            <div className="form-group" style={{ marginBottom: '30px' }}>
              <label className="form-label" htmlFor="preferred_country">Preferred Destination *</label>
              <select
                id="preferred_country"
                className={`form-input ${errors.preferred_country ? 'form-input--error' : ''}`}
                style={{ height: '40px' }}
                {...register('preferred_country')}
              >
                <option value="">Select Target Country</option>
                {countries.map(c => (
                  <option key={c._id} value={c.name}>{c.name}</option>
                ))}
              </select>
              {errors.preferred_country && <span className="form-error">{errors.preferred_country.message}</span>}
            </div>

            <button type="submit" disabled={loading} className="btn btn-primary" style={{ width: '100%', height: '44px' }}>
              {loading ? 'Creating Account...' : 'Register Candidate'} <ArrowRight size={16} style={{ marginLeft: '6px' }} />
            </button>
            
            <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '0.825rem', color: theme.colors.text.muted }}>
              Already registered? <a href="/login" style={{ color: theme.colors.primary, fontWeight: 600 }}>Login here</a>
            </div>
          </form>
        </div>

      </div>
    </div>
  );
};
